/**
 * QoCAwards.js
 * Presentation-ready Clause Champions / Awards page.
 * Shows top performing facility per clause for a given period,
 * plus an overall "Facility of the Period" grand prize.
 *
 * Route suggestion: /qoc/awards
 *
 * Design note: this page is meant to be opened in full-screen / projected
 * during an actual award ceremony, so it favours large, celebratory visuals
 * (medals, big facility names) over data density.
 */
import React, { useState, useEffect } from 'react';
import util from '../../utils/util';
import { Select, Spin, Empty, Button, Tag, InputNumber, Tooltip, Radio } from 'antd';
import {
    TrophyOutlined, CrownOutlined, StarFilled, ReloadOutlined,
    FullscreenOutlined, FilePdfOutlined, LoadingOutlined,
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import moment from 'moment';
import AYSService from '../../services/AYSService';

const MEDAL = ['🥇', '🥈', '🥉'];
const MEDAL_COLOR = ['#d4af37', '#a8a8a8', '#b08d57'];

const RATING_LABEL = (score) => {
    if (score >= 98) return { label: 'Outstanding', color: '#389e0d' };
    if (score >= 95) return { label: 'Excellent', color: '#1890ff' };
    if (score >= 90) return { label: 'Good', color: '#52c41a' };
    if (score >= 85) return { label: 'Fair', color: '#faad14' };
    if (score >= 70) return { label: 'Requires Improvement', color: '#fa8c16' };
    return { label: 'Inadequate', color: '#f5222d' };
};

function PodiumCard({ champion, podiumSize = 3 }) {
    const podium = (champion.podium || []).slice(0, podiumSize);
    const hasWinner = podium.length > 0;
    const rating = hasWinner ? RATING_LABEL(podium[0].score) : null;

    return (
        <div style={{
            background: '#fff', borderRadius: 16, padding: 20,
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            border: hasWinner ? `2px solid ${MEDAL_COLOR[0]}55` : '1px solid #eee',
            minHeight: 280, display: 'flex', flexDirection: 'column',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Tag style={{ background: '#1890ff', color: '#fff', border: 'none', fontWeight: 800, fontSize: 13 }}>
                    {champion.clause_code}
                </Tag>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#333', flex: 1, lineHeight: 1.3 }}>
                    {champion.clause_title}
                </div>
            </div>

            {!hasWinner ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 28, marginBottom: 6 }}>—</div>
                        <div style={{ fontSize: 12 }}>No eligible facilities yet</div>
                    </div>
                </div>
            ) : (
                <div style={{ flex: 1 }}>
                    {podium.map((p, i) => (
                        <div key={p.facility_id} style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 8px', marginBottom: 6,
                            background: i === 0 ? `${MEDAL_COLOR[0]}10` : '#fafafa',
                            borderRadius: 10,
                            border: i === 0 ? `1px solid ${MEDAL_COLOR[0]}40` : '1px solid #f0f0f0',
                        }}>
                            <span style={{ fontSize: i === 0 ? 26 : 20, width: 32, textAlign: 'center', flexShrink: 0 }}>
                                {MEDAL[i] || ''}
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                    fontWeight: i === 0 ? 800 : 600, fontSize: i === 0 ? 14 : 12.5,
                                    color: i === 0 ? '#1a1a2e' : '#555',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {p.facility_name}
                                </div>
                                <div style={{ fontSize: 10, color: '#999' }}>
                                    {p.region} · {p.assessment_count} assessment{p.assessment_count !== 1 ? 's' : ''}
                                </div>
                            </div>
                            <div style={{ fontWeight: 900, fontSize: i === 0 ? 18 : 14, color: MEDAL_COLOR[i] || '#888' }}>
                                {p.score.toFixed(1)}%
                            </div>
                        </div>
                    ))}
                    {champion.winners && champion.winners.length > 1 && (
                        <div style={{ fontSize: 10, color: '#d4af37', fontWeight: 700, marginTop: 4, textAlign: 'center' }}>
                            🏆 Joint winners — tied score
                        </div>
                    )}
                </div>
            )}

            {hasWinner && (
                <div style={{ marginTop: 10, textAlign: 'center' }}>
                    <Tag style={{ background: rating.color, color: '#fff', border: 'none', fontSize: 10 }}>
                        {rating.label}
                    </Tag>
                </div>
            )}
        </div>
    );
}

function GrandPrizeBanner({ champion, periodLabel, rankMode }) {
    if (!champion) return null;
    const isQI = rankMode === 'qi';
    const headlineScore = isQI ? champion.quality_index : champion.avg_score;
    const rating = RATING_LABEL(headlineScore);
    return (
        <div style={{
            background: 'linear-gradient(135deg,#1e293b,#0f172a)',
            borderRadius: 20, padding: '36px 40px', marginBottom: 32,
            color: '#fff', textAlign: 'center', position: 'relative', overflow: 'hidden',
        }}>
            <div style={{ position: 'absolute', top: -20, right: -20, fontSize: 160, opacity: 0.06 }}>
                <CrownOutlined />
            </div>
            <div style={{ fontSize: 12, letterSpacing: 3, opacity: 0.7, marginBottom: 10, textTransform: 'uppercase' }}>
                Facility of the Period · {periodLabel} · {isQI ? 'Quality Index' : 'Audit Score'} Ranking
            </div>
            <CrownOutlined style={{ fontSize: 40, color: '#facc15', marginBottom: 10 }} />
            <div style={{ fontSize: 30, fontWeight: 900, marginBottom: 6 }}>
                {champion.facility_name}
            </div>
            <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 14 }}>
                {champion.region} · {champion.site_type}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 28, flexWrap: 'wrap' }}>
                <div>
                    <div style={{ fontSize: 36, fontWeight: 900, color: '#facc15', lineHeight: 1 }}>
                        {headlineScore.toFixed(1)}%
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.7 }}>{isQI ? 'Quality Index' : 'Overall Average'}</div>
                </div>
                {isQI && (
                    <>
                        <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)' }} />
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>
                                {champion.qoc_score.toFixed(1)}%
                            </div>
                            <div style={{ fontSize: 11, opacity: 0.7 }}>Audit Score ({champion.qoc_weight}%)</div>
                        </div>
                        <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)' }} />
                        <div>
                            <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>
                                {champion.survey_composite.toFixed(1)}%
                            </div>
                            <div style={{ fontSize: 11, opacity: 0.7 }}>Patient Survey ({champion.survey_weight}%)</div>
                        </div>
                    </>
                )}
                <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)' }} />
                <div>
                    <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1 }}>
                        {champion.assessment_count}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.7 }}>Assessments</div>
                </div>
            </div>
            <Tag style={{ background: rating.color, color: '#fff', border: 'none', marginTop: 16, fontSize: 11 }}>
                {rating.label}
            </Tag>
            {isQI && (champion.inpatient_count + champion.outpatient_count) === 0 && (
                <div style={{ fontSize: 11, color: '#fca5a5', marginTop: 10 }}>
                    ⚠ No patient surveys recorded yet — survey component currently scoring 0%
                </div>
            )}
        </div>
    );
}

function OverallLeaderboard({ list, rankMode, podiumSize = 3 }) {
    const isQI = rankMode === 'qi';
    const rows = (list || []).slice(0, podiumSize);
    if (rows.length <= 1) return null; // grand prize banner already covers #1 with only one entry

    return (
        <div style={{
            background: '#fff', borderRadius: 16, padding: '16px 20px', marginBottom: 32,
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#555', marginBottom: 12,
                textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {isQI ? 'Quality Index' : 'Audit Score'} — Top {rows.length}
            </div>
            {rows.map((f, i) => {
                const score = isQI ? f.quality_index : f.avg_score;
                const rating = RATING_LABEL(score);
                return (
                    <div key={f.facility_id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 8px', marginBottom: 6,
                        background: i === 0 ? '#fffbe6' : '#fafafa',
                        borderRadius: 10,
                        border: i === 0 ? '1px solid #facc1540' : '1px solid #f0f0f0',
                    }}>
                        <span style={{ fontSize: i < 3 ? 22 : 16, width: 30, textAlign: 'center', flexShrink: 0 }}>
                            {MEDAL[i] || `#${i + 1}`}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                fontWeight: i === 0 ? 800 : 600, fontSize: i === 0 ? 14 : 13,
                                color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                                {f.facility_name}
                            </div>
                            <div style={{ fontSize: 11, color: '#999' }}>
                                {f.region} · {f.site_type} · {f.assessment_count} assessment{f.assessment_count !== 1 ? 's' : ''}
                            </div>
                        </div>
                        {isQI && (
                            <div style={{ fontSize: 10, color: '#aaa', textAlign: 'right', marginRight: 8 }}>
                                Audit {parseFloat(f.qoc_score || 0).toFixed(1)}%<br/>
                                Survey {parseFloat(f.survey_composite || 0).toFixed(1)}%
                            </div>
                        )}
                        <div style={{ fontWeight: 900, fontSize: i === 0 ? 18 : 15, color: rating.color, minWidth: 64, textAlign: 'right' }}>
                            {score.toFixed(1)}%
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ── ENHEA Category Rankings ───────────────────────────────────────────────────
function ENHEACategoryView({ data, categories, loading, periodLabel }) {
    // Group by category
    const grouped = {};
    data.forEach(row => {
        const key = row.category_code || 'OTHER';
        if (!grouped[key]) grouped[key] = { category_name: row.category_name, code: key, facilities: [] };
        grouped[key].facilities.push(row);
    });

    const MEDALS = ['🥇', '🥈', '🥉'];
    const CAT_COLORS = {
        TERTIARY: '#7c3aed', REGIONAL: '#1d4ed8', HEALTH_CENTRE: '#0891b2',
        CLINIC: '#0369a1', PROGRAM: '#065f46', PATIENT_SAFETY: '#92400e',
        INDIVIDUAL: '#9f1239', ADMIN_DEPT: '#374151', CHM: '#166534',
    };

    if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;

    if (data.length === 0) return (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏥</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>No Facility Assessments Found</div>
            <div style={{ fontSize: 13, maxWidth: 500, margin: '0 auto' }}>
                No approved facility assessments found for period <strong>{periodLabel || 'ENHEA-2026'}</strong>.<br/>
                Schedule and complete QoC assessments using the ENHEA-2026 period, then approve them to populate this view.
            </div>
        </div>
    );

    return (
        <div>
            {/* Judge validation status */}
            <div style={{ background: '#fefce8', border: '1px solid #fbbf24', borderRadius: 8, padding: '12px 20px', marginBottom: 24, display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ fontSize: 18 }}>⚖️</span>
                <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>Judge Validation Status</div>
                    <div style={{ fontSize: 12, color: '#78716c' }}>
                        Scores shown = 60% NQMP assessment + 40% judge average.
                        Facilities without judge scores show NQMP score only.
                        Go to <b>Health Awards → Judge Portal</b> to enter judge scores.
                    </div>
                </div>
            </div>

            {/* Category cards */}
            {categories.filter(cat => grouped[cat.code]).map(cat => {
                const group = grouped[cat.code];
                if (!group) return null;
                const color = CAT_COLORS[cat.code] || '#374151';
                const sorted = [...group.facilities].sort((a, b) => b.final_score - a.final_score);
                return (
                    <div key={cat.code} style={{ marginBottom: 28 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 16px', background: color, borderRadius: 8 }}>
                            <span style={{ fontSize: 20 }}>🏆</span>
                            <div style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>{cat.name}</div>
                            <div style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>{sorted.length} facilit{sorted.length !== 1 ? 'ies' : 'y'}</div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                            {sorted.map((f, i) => (
                                <div key={f.assessment_id} style={{
                                    background: '#fff', borderRadius: 10, padding: '14px 18px',
                                    border: i === 0 ? `2px solid ${color}` : '1px solid #e5e7eb',
                                    boxShadow: i === 0 ? `0 4px 12px ${color}33` : 'none',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <span style={{ fontSize: 20 }}>{MEDALS[i] || `#${i+1}`}</span>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 700, fontSize: 13 }}>{f.facility_name}</div>
                                            <div style={{ fontSize: 11, color: '#6b7280' }}>{f.region} · {f.site_type}</div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: 22, fontWeight: 800, color: i === 0 ? color : '#374151' }}>
                                                {parseFloat(f.final_score || 0).toFixed(1)}%
                                            </div>
                                            <div style={{ fontSize: 10, color: '#9ca3af' }}>Final score</div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                                        <span style={{ background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>
                                            NQMP: {parseFloat(f.nqmp_score || 0).toFixed(1)}%
                                        </span>
                                        {f.judge_avg_pct != null && (
                                            <span style={{ background: '#fef3c7', padding: '2px 8px', borderRadius: 4 }}>
                                                Judges: {parseFloat(f.judge_avg_pct).toFixed(1)}%
                                            </span>
                                        )}
                                        {(!f.judge_avg_pct) && (
                                            <span style={{ background: '#fee2e2', padding: '2px 8px', borderRadius: 4, color: '#dc2626' }}>
                                                Awaiting judges
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}


// ── Programs Track View ───────────────────────────────────────────────────────
function ProgramsTrackView({ data, loading }) {
    const { Spin, Empty, Tag } = require('antd');

    const BANDS = {
        'Best Practice Champion': { color: '#16a34a', bg: '#f0fdf4', min: 60 },
        'Strong Performer':       { color: '#2563eb', bg: '#eff6ff', min: 45 },
        'Developing Performer':   { color: '#d97706', bg: '#fffbeb', min: 30 },
        'Needs Support':          { color: '#dc2626', bg: '#fef2f2', min: 0  },
    };

    const MEDALS = ['🥇', '🥈', '🥉'];

    // Group by award category
    const grouped = {};
    data.forEach(row => {
        const key = row.ha_category_name || 'Uncategorised';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(row);
    });

    // Sort each group by score desc
    Object.values(grouped).forEach(g => g.sort((a,b) => (b.program_score||0) - (a.program_score||0)));

    if (loading) return <div style={{ textAlign:'center', padding:60 }}><Spin size="large" /></div>;

    if (data.length === 0) return (
        <Empty description="No approved Programs assessments found for this period. Complete and approve assessments under Programs Assessment to populate this view." />
    );

    return (
        <div>
            {/* Band legend */}
            <div style={{ display:'flex', gap:8, marginBottom:24, flexWrap:'wrap' }}>
                {Object.entries(BANDS).map(([band, s]) => (
                    <span key={band} style={{ fontSize:11, padding:'3px 10px', borderRadius:4,
                        color:s.color, background:s.bg, border:`1px solid ${s.color}`, fontWeight:600 }}>
                        {band} ({s.min}+/70)
                    </span>
                ))}
            </div>

            {Object.entries(grouped).map(([categoryName, programs]) => (
                <div key={categoryName} style={{ marginBottom:28 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12,
                        padding:'8px 16px', background:'#422006', borderRadius:8 }}>
                        <span style={{ fontSize:20 }}>📂</span>
                        <div style={{ color:'#fff', fontWeight:800, fontSize:15 }}>{categoryName}</div>
                        <div style={{ marginLeft:'auto', fontSize:11, color:'rgba(255,255,255,0.6)' }}>
                            {programs.length} program{programs.length !== 1 ? 's' : ''}
                        </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:12 }}>
                        {programs.map((p, i) => {
                            const score  = parseFloat(p.program_score || 0);
                            const band   = p.program_band || 'Needs Support';
                            const bStyle = BANDS[band] || BANDS['Needs Support'];
                            return (
                                <div key={p.id} style={{
                                    background:'#fff', borderRadius:10, padding:'14px 18px',
                                    border: i === 0 ? `2px solid ${bStyle.color}` : '1px solid #e5e7eb',
                                    boxShadow: i === 0 ? `0 4px 12px ${bStyle.color}33` : 'none',
                                }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                                        <span style={{ fontSize:20 }}>{MEDALS[i] || `#${i+1}`}</span>
                                        <div style={{ flex:1 }}>
                                            <div style={{ fontWeight:700, fontSize:13 }}>{p.program_name}</div>
                                            <div style={{ fontSize:11, color:'#6b7280' }}>
                                                {p.program_code} · {p.program_category}
                                            </div>
                                        </div>
                                        <div style={{ textAlign:'right' }}>
                                            <div style={{ fontSize:24, fontWeight:800, color:bStyle.color }}>
                                                {score.toFixed(0)}
                                            </div>
                                            <div style={{ fontSize:10, color:'#9ca3af' }}>{parseFloat(p.overall_percentage||0).toFixed(1)}%</div>
                                        </div>
                                    </div>
                                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                                        <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4,
                                            color:bStyle.color, background:bStyle.bg,
                                            border:`1px solid ${bStyle.color}`, fontWeight:700 }}>
                                            {band}
                                        </span>
                                        <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4,
                                            background:'#f3f4f6', color:'#374151' }}>
                                            {parseFloat(p.overall_percentage || 0).toFixed(1)}%
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}


export default function QoCAwards() {
    const [loading, setLoading] = useState(false);
    // Permission checks
    const canAdd    = util.canAdd('prog_admin');
    const canEdit   = util.canEdit('prog_admin');
    const canDelete = util.canDelete('prog_admin');
    const canExport = util.canExport('prog_admin');
    const [periods, setPeriods] = useState([]);
    const [periodLabel, setPeriodLabel] = useState(null);
    const [minAssessments, setMinAssessments] = useState(1);
    const [champions, setChampions] = useState([]);
    const [overallChampions, setOverallChampions] = useState([]);
    const [clauseFilter, setClauseFilter] = useState([]); // empty = show all clauses
    const [enheaClauses, setEnheaClauses]   = useState([]);
    const [podiumSize, setPodiumSize] = useState(3); // facilities shown per clause card
    const [rankMode, setRankMode] = useState('qi'); // 'qi' = Quality Index, 'audit' = raw audit score
    const [overallAudit, setOverallAudit] = useState([]); // audit-ranked overall list (fallback / comparison view)
    const [overallQI, setOverallQI] = useState([]); // QI-ranked overall list
    // ── Health Awards mode ────────────────────────────────────────────────
    const [pageMode, setPageMode]           = useState('enhea');
    const [track, setTrack]                 = useState('facility'); // 'facility' | 'programs'
    const [programsData, setProgramsData]   = useState([]);
    const [programsLoading, setProgramsLoading] = useState(false);
    const [haData, setHaData]               = useState([]);
    const [haLoading, setHaLoading]         = useState(false);
    const [haCategories, setHaCategories]   = useState([]);

    useEffect(() => {
        QoCService.getClauses().then(r => {
            const cls = (r.data?.data || []).filter(c => c.code && c.code.startsWith('ENHEA'));
            setEnheaClauses(cls);
        });
        QoCService.getPeriods().then(r => {
            const active = (r.data?.data || []).filter(p => parseInt(p.is_active) === 1);
            setPeriods(active);
            // Default to first ENHEA period since this page is awards-only
            const enheaPeriod = active.find(p => p.label.startsWith('ENHEA'));
            if (enheaPeriod) setPeriodLabel(enheaPeriod.label);
        });
    }, []);

    useEffect(() => { if (periodLabel) { load(); setClauseFilter([]); } }, [periodLabel, minAssessments]); // eslint-disable-line

    // Load Health Awards consolidated scores
    const loadHA = () => {
        setHaLoading(true);
        Promise.all([
            AYSService.getConsolidatedScores({ period_label: periodLabel || 'ENHEA-2026' }),
            AYSService.getHaCategories(),
        ]).then(([scoresRes, catRes]) => {
            setHaData(scoresRes.data?.data || []);
            setHaCategories(catRes.data?.data || []);
        }).catch(() => {})
        .finally(() => setHaLoading(false));
    };

    useEffect(() => { if (pageMode === 'enhea') loadHA(); }, [pageMode, periodLabel]); // eslint-disable-line

    // Load Programs track data
    const loadPrograms = () => {
        setProgramsLoading(true);
        import('./../../services/ProgramsService').then(({ default: ProgramsService }) => {
            ProgramsService.getAssessments({ period_label: periodLabel || 'ENHEA-2026', status: 'approved' })
                .then(r => setProgramsData(r.data?.data || []))
                .catch(() => {})
                .finally(() => setProgramsLoading(false));
        });
    };

    useEffect(() => { if (track === 'programs' && periodLabel) loadPrograms(); }, [track, periodLabel]); // eslint-disable-line

    const load = async () => {
        setLoading(true);
        try {
            const filters = { period_label: periodLabel, min_assessments: minAssessments };
            const [champRes, auditRes, qiRes] = await Promise.all([
                QoCService.getClauseChampions ? QoCService.getClauseChampions(filters) : Promise.resolve({ data: { data: [] } }),
                QoCService.getOverallChampion ? QoCService.getOverallChampion(filters) : Promise.resolve({ data: { data: [] } }),
                QoCService.getOverallChampionByQI ? QoCService.getOverallChampionByQI(filters) : Promise.resolve({ data: { data: [] } }),
            ]);
            setChampions(champRes.data?.data || []);
            setOverallAudit(auditRes.data?.data || []);
            setOverallQI(qiRes.data?.data || []);
            // keep legacy state populated too, mirrors whichever the UI is currently using
            setOverallChampions(auditRes.data?.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const qiUnavailable = rankMode === 'qi' && overallQI.length === 0;
    const effectiveRankMode = qiUnavailable ? 'audit' : rankMode;
    const grandPrize = effectiveRankMode === 'qi' ? (overallQI[0] || null) : (overallAudit[0] || null);
    const displayedChampions = clauseFilter.length
        ? champions.filter(c => clauseFilter.includes(c.clause_id))
        : champions;

    return (
        <div className="page-content">
            {/* Header */}
            <div style={{
                background: 'linear-gradient(135deg,#0f172a,#1e293b)',
                padding: '20px 32px', display: 'flex', alignItems: 'center',
                gap: 14, flexWrap: 'wrap',
            }}>
                <TrophyOutlined style={{ fontSize: 28, color: '#facc15' }} />
                <div style={{ flex: 1 }}>
                    <h2 style={{ margin: 0, color: '#fff', fontSize: 20, fontWeight: 800 }}>
                        {'🏆 Health Excellence Awards' + (periodLabel ? ' — ' + periodLabel : '')}
                    </h2>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                        Eswatini Ministry of Health · National Quality Management Programme
                    </div>
                </div>

                {pageMode === 'enhea' && (
                    <Select size="small" style={{ width: 180 }} placeholder="Select ENHEA period"
                        value={periodLabel} onChange={setPeriodLabel}>
                        {periods.filter(p => p.label.startsWith('ENHEA')).map(p => (
                            <Select.Option key={p.label} value={p.label}>
                                {p.label}
                            </Select.Option>
                        ))}
                    </Select>
                )}



                
                <Select mode="multiple" size="small" allowClear
                    placeholder="All ENHEA clauses"
                    style={{ minWidth: 200, maxWidth: 340 }}
                    maxTagCount={2}
                    value={clauseFilter}
                    onChange={setClauseFilter}>
                    {enheaClauses.map(c => (
                        <Select.Option key={c.id} value={c.id}>
                            {c.code} — {c.title}
                        </Select.Option>
                    ))}
                </Select>
                <Button size="small" icon={<ReloadOutlined />}
                    onClick={track === 'programs' ? loadPrograms : loadHA}
                    loading={loading || haLoading || programsLoading}>
                    Refresh
                </Button>
                <Button size="small" icon={<FullscreenOutlined />}
                    onClick={() => document.documentElement.requestFullscreen?.()}>
                    Present
                </Button>
            </div>

            {/* Track selector */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 32px', display: 'flex', gap: 0 }}>
                {[
                    { key: 'facility', label: '🏥 Facility Awards', desc: 'Ranked by QoC assessment score' },
                    { key: 'programs', label: '📂 Programs & Units', desc: 'Ranked by Programs Assessment score' },
                    { key: 'individual', label: '👤 Individual Awards', desc: 'Manual process for 2026' },
                ].map(t => (
                    <div key={t.key} onClick={() => setTrack(t.key)} style={{
                        padding: '12px 24px', cursor: 'pointer',
                        borderBottom: track === t.key ? '3px solid #d97706' : '3px solid transparent',
                        background: track === t.key ? '#fffbeb' : 'transparent',
                    }}>
                        <div style={{ fontWeight: track === t.key ? 700 : 500, fontSize: 14,
                            color: track === t.key ? '#92400e' : '#595959' }}>{t.label}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>{t.desc}</div>
                    </div>
                ))}
            </div>

            <div style={{ background: '#f4f6fb', padding: '32px', minHeight: '70vh' }}>
                {track === 'facility' && (
                    <ENHEACategoryView
                        data={haData}
                        categories={haCategories}
                        loading={haLoading}
                        periodLabel={periodLabel}
                        clauseFilter={clauseFilter}
                        enheaClauses={enheaClauses} />
                )}

                {track === 'programs' && (
                    <ProgramsTrackView data={programsData} loading={programsLoading} />
                )}

                {track === 'individual' && (
                    <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>👤</div>
                        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Individual & CHM Awards</div>
                        <div style={{ fontSize: 14 }}>Manual process for 2026. System support coming in 2027.</div>
                    </div>
                )}
                {false && <Spin spinning={loading}>
                    {!periodLabel ? (
                        <Empty description="Select a period to view champions" />
                    ) : (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                                <Radio.Group size="small" value={rankMode} onChange={e => setRankMode(e.target.value)}
                                    buttonStyle="solid">
                                    <Radio.Button value="qi">⭐ Quality Index (Audit + Survey)</Radio.Button>
                                    <Radio.Button value="audit">📋 Audit Score Only</Radio.Button>
                                </Radio.Group>
                            </div>
                            
                            <GrandPrizeBanner champion={grandPrize} periodLabel={periodLabel} rankMode={effectiveRankMode} />
                            <OverallLeaderboard
                                list={effectiveRankMode === 'qi' ? overallQI : overallAudit}
                                rankMode={effectiveRankMode}
                                podiumSize={podiumSize}
                            />

                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20,
                            }}>
                                <StarFilled style={{ color: '#faad14', fontSize: 18 }} />
                                <div style={{ fontWeight: 800, fontSize: 16, color: '#1a1a2e' }}>
                                    Clause Champions — {periodLabel}
                                </div>
                                <Tag color="blue" style={{ marginLeft: 'auto' }}>
                                    {displayedChampions.length} of {champions.length} clause{champions.length !== 1 ? 's' : ''}
                                </Tag>
                            </div>

                            {displayedChampions.length === 0 ? (
                                <Empty description={champions.length === 0
                                    ? "No eligible facilities for this period yet"
                                    : "No clauses match the current filter"} />
                            ) : (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: 18,
                                }}>
                                    {displayedChampions.map(c => (
                                        <PodiumCard key={c.clause_id} champion={c} podiumSize={podiumSize} />
                                    ))}
                                </div>
                            )}

                            <div style={{ textAlign: 'center', marginTop: 40, fontSize: 11, color: '#aaa' }}>
                                Generated {moment().format('DD MMMM YYYY, HH:mm')} · Eligibility: minimum {minAssessments} approved assessment{minAssessments !== 1 ? 's' : ''} per clause this period
                            </div>
                        </>
                    )}
                </Spin>}
            </div>
        </div>
    );
}

